import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Copy,
  FileSpreadsheet,
  FileText,
  Pencil,
  Plus,
  Printer,
  Search,
  Trash2,
  Eye,
  ArrowRightLeft,
  X,
  MessageCircle,
  Loader,
  Sparkles,
  SearchX,
  FileStack,
} from "lucide-react";
import { MenuContainer, ThreeDotsButton, Dot, DropdownMenu, MenuItem, ItemIconContainer, ItemLabel, Backdrop, ToolbarLeft, ToolbarRight } from "./documentSharedStyles";
import ExportDropdown from "../../atoms/Dashboard/ExportDropdown";
import PermissionGate from "../../atoms/Utlis/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { device } from "../../atoms/Utlis/media";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { getAllQuotationAPI, deleteQuotationAPI, convertQuotationToOrderAPI, getQuotationByIdAPI, shareQuotationOnWhatsAppAPI } from "../../../Redux/ApiCalls/Dashboard/QuotationAPI";
import DeletePopup from "../../atoms/Dashboard/Folder/DeletePopup";
import { copyDataToClipboard, exportDataToCSV, exportDataToExcel } from "../../../Utils/exportHelpers";
import { exportElementAsPDF } from "../../../Utils/pdfHelpers";
import { formatINR } from "../../../Utils/helper";
import { DocumentPrintTemplate } from "./DocumentPrintTemplate";
import { ListReportPrintTemplate, CellBold } from "./ListReportPrintTemplate";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import QuotationShareModal from "./QuotationShareModal";
import StudioTermsManageModal from "./StudioTermsManageModal";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import { SubmitButton } from "../../molecules/Dashboard/Member/memberFormStyles";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroRow,
  HeroText as ModalHeroText,
  HeroBadge as ModalHeroBadge,
  ModalTitle as ConfirmModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ListHeader,
  ListTitle,
  CountBadge,
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
  AmountCell,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

type QuotationRecord = {
  id: number;
  quotation_no: string;
  customer_name: string;
  customer_phone: string;
  customer_reference?: string;
  customer_place?: string;
  quotation_date: string;
  grand_total: string;
  without_price?: boolean;
  status?: string;
};

const AllQuotationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { allQuotations, totalCount, loading } = useAppSelector((state) => state.quotation);

  const canViewQuotation = hasPermission("quotation.read");
  const canEditQuotation = hasPermission("quotation.update");
  const canConvertQuotation = hasAnyPermission(["orders.create", "quotation.update"]);
  const canShareQuotation = hasPermission("quotation.read");
  const canDeleteQuotation = hasPermission("quotation.delete");
  const hasAnyQuotationAction =
    canViewQuotation || canEditQuotation || canConvertQuotation || canShareQuotation || canDeleteQuotation;

  const [searchVal, setSearchVal] = useState("");
  const [searchText, setSearchText] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [convertConfirmId, setConvertConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const menuButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const { user, studio_logo, business, selectedBranch } = useAppSelector((state) => state.auth);
  const [sharingQuotationId, setSharingQuotationId] = useState<number | null>(null);
  const [revertedQuotationIds, setRevertedQuotationIds] = useState<number[]>([]);
  const [activeQuotationForShare, setActiveQuotationForShare] = useState<any>(null);
  const [modalShareQuotation, setModalShareQuotation] = useState<any>(null);
  const sharePrintRef = useRef<HTMLDivElement>(null);
  const reportPrintRef = useRef<HTMLDivElement>(null);
  const [isStudioTermsModalOpen, setIsStudioTermsModalOpen] = useState(false);

  const hasStudioTerms = Boolean(business?.terms_and_conditions && business.terms_and_conditions.trim());

  const handleShareQuotation = async (qId: number) => {
    try {
      const fullQuotation = await getQuotationByIdAPI(qId);
      if (!fullQuotation) {
        showErrorToast("Quotation not found");
        return;
      }
      setModalShareQuotation(fullQuotation);
    } catch (err) {
      showErrorToast("Failed to fetch quotation for sharing.");
    }
  };

  const handleOfficialShare = async () => {
    if (!modalShareQuotation) return;
    setSharingQuotationId(modalShareQuotation.id);
    try {
      setActiveQuotationForShare(modalShareQuotation);

      // Give it a moment to render in the hidden div
      await new Promise((resolve) => setTimeout(resolve, 600));

      if (sharePrintRef.current) {
        const blob = await exportElementAsPDF(sharePrintRef.current);
        const filename = `Quotation_${modalShareQuotation.quotation_no}.pdf`;
        const result = await shareQuotationOnWhatsAppAPI(modalShareQuotation.id, blob, filename);
        showSuccessToast(`Quotation sent to ${result.sent_to || modalShareQuotation.customer_phone} on WhatsApp 🎉`);
      } else {
        showErrorToast("Print template not ready. Try again.");
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to share on WhatsApp.");
    } finally {
      setSharingQuotationId(null);
      setActiveQuotationForShare(null);
      setModalShareQuotation(null);
    }
  };

  const fetchQuotations = () => {
    dispatch(getAllQuotationAPI({ page: currentPage, page_size: entries, search: searchText, branch_id: selectedBranch?.id }));
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchVal);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchVal]);

  useEffect(() => {
    fetchQuotations();
  }, [dispatch, currentPage, entries, searchText, selectedBranch?.id]);

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmId === null) return;
    try {
      setDeleting(true);
      await deleteQuotationAPI(deleteConfirmId);
      showSuccessToast("Quotation deleted successfully!");
      fetchQuotations();
    } catch (err: any) {
      showErrorToast("Failed to delete quotation");
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const handleConvertToOrder = (id: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setConvertConfirmId(id);
  };

  const handleViewConvertedOrder = async (q: any) => {
    try {
      let res = await apiCall({
        method: "GET",
        url: `/order/orders/?search=${q.quotation_no}`
      });
      let orders = res?.results || res || [];
      let matchedOrder = orders.find((o: any) =>
        o.quotation === q.id ||
        String(o.quotation_id) === String(q.id) ||
        o.quotation_no === q.quotation_no ||
        o.reference_no === q.quotation_no ||
        o.order_no === q.quotation_no
      );

      if (!matchedOrder && q.customer_phone) {
        const rawDigits = q.customer_phone.replace(/\D/g, "");
        const querySearch = rawDigits.length >= 10 ? rawDigits.slice(-10) : q.customer_phone;
        res = await apiCall({
          method: "GET",
          url: `/order/orders/?search=${encodeURIComponent(querySearch)}`
        });
        const phoneOrders = res?.results || res || [];
        matchedOrder = phoneOrders.find((o: any) =>
          o.quotation === q.id ||
          String(o.quotation_id) === String(q.id) ||
          o.quotation_no === q.quotation_no ||
          o.reference_no === q.quotation_no ||
          o.order_no === q.quotation_no ||
          (rawDigits.length >= 10 && (o.customer_phone?.replace(/\D/g, "").slice(-10) === rawDigits.slice(-10) || o.customer_details?.phone?.replace(/\D/g, "").slice(-10) === rawDigits.slice(-10)))
        );
      }

      if (matchedOrder && matchedOrder.id) {
        navigate(`/dashboard/orders/view?id=${matchedOrder.id}`);
      } else {
        showErrorToast("The converted order has been deleted.");
        setRevertedQuotationIds(prev => [...prev, q.id]);
      }
    } catch (err) {
      showErrorToast("Failed to locate converted order");
    }
  };

  const handleConfirmConvert = async () => {
    if (convertConfirmId === null) return;
    try {
      setConverting(true);
      const response = await convertQuotationToOrderAPI(convertConfirmId);
      showSuccessToast("Quotation converted to Order successfully!");
      navigate("/dashboard/orders/all");
    } catch (err: any) {
      showErrorToast(err?.message || err?.error || "Failed to convert quotation to order");
    } finally {
      setConverting(false);
      setConvertConfirmId(null);
    }
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    copyDataToClipboard(
      allQuotations,
      ["Quotation No", "Customer Name", "Mobile", "Place", "Grand Total"],
      ["quotation_no", "customer_name", "customer_phone", "customer_place", "grand_total"],
      "Quotation list copied to clipboard"
    );
  };

  const handleExportCSV = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    exportDataToCSV(
      allQuotations,
      ["Quotation No", "Customer Name", "Mobile", "Reference", "Place", "Created On", "Grand Total"],
      ["quotation_no", "customer_name", "customer_phone", "customer_reference", "customer_place", "quotation_date", "grand_total"],
      "quotations_export.csv",
      "CSV exported successfully"
    );
  };

  const handleExportExcel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    exportDataToExcel(
      allQuotations,
      ["Quotation No", "Customer Name", "Mobile", "Reference", "Place", "Created On", "Grand Total"],
      ["quotation_no", "customer_name", "customer_phone", "customer_reference", "customer_place", "quotation_date", "grand_total"],
      "quotations_export.xls",
      "Excel exported successfully"
    );
  };

  const quotationReportRows = useMemo(
    () =>
      allQuotations.map((q) => ({
        quotation_no: q.quotation_no,
        quotation_date: q.quotation_date,
        customer_name: q.customer_name,
        customer_phone: q.customer_phone,
        customer_reference: q.customer_reference || '—',
        customer_place: q.customer_place || '—',
        grand_total: q.without_price ? '—' : `₹${formatINR(q.grand_total)}`,
        total_raw: q.without_price ? 0 : Number(q.grand_total) || 0,
      })),
    [allQuotations]
  );

  const quotationReportSummary = useMemo(() => {
    const totalValue = quotationReportRows.reduce((sum, row) => sum + row.total_raw, 0);
    return [
      { label: 'Total Quotations', value: String(quotationReportRows.length) },
      { label: 'Total Quoted Value', value: `₹${formatINR(totalValue)}`, highlight: true },
    ];
  }, [quotationReportRows]);

  const handleExportPDF = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      if (!reportPrintRef.current) {
        showErrorToast("Report template not ready");
        return;
      }

      const blob = await exportElementAsPDF(reportPrintRef.current);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "quotations_report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccessToast("PDF exported successfully");
    } catch (err) {
      showErrorToast("Failed to generate PDF");
    }
  };

  const visibleQuotations = allQuotations;

  const totalFilteredCount = totalCount;

  const totalPages = Math.ceil(totalFilteredCount / entries) || 1;
  const hasActiveSearch = !!searchText;

  const handleToggleMenu = useCallback((id: number) => {
    if (activeMenuId === id) {
      setActiveMenuId(null);
      setMenuCoords(null);
      return;
    }

    const btn = menuButtonRefs.current[id];
    if (!btn) {
      setActiveMenuId(id);
      return;
    }

    const btnRect = btn.getBoundingClientRect();
    const estimatedMenuHeight = 280;
    const gap = 8;
    const spaceBelow = window.innerHeight - btnRect.bottom;
    const spaceAbove = btnRect.top;
    const openUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
    const right = Math.max(8, window.innerWidth - btnRect.right);

    setMenuCoords(
      openUp
        ? { bottom: window.innerHeight - btnRect.top + gap, right }
        : { top: btnRect.bottom + gap, right }
    );
    setActiveMenuId(id);
  }, [activeMenuId]);

  useEffect(() => {
    if (activeMenuId === null) return;

    const closeMenu = () => {
      setActiveMenuId(null);
      setMenuCoords(null);
    };

    const scrollEl = scrollRef.current;
    scrollEl?.addEventListener("scroll", closeMenu, { passive: true });
    window.addEventListener("resize", closeMenu);
    return () => {
      scrollEl?.removeEventListener("scroll", closeMenu);
      window.removeEventListener("resize", closeMenu);
    };
  }, [activeMenuId]);

  const activeMenuQuotation = useMemo(
    () => visibleQuotations.find((q) => q.id === activeMenuId) ?? null,
    [visibleQuotations, activeMenuId]
  );

  return (
    <ScrollPage>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Customer quotes
              </HeroBadge>
              <PageTitle className="linear-text">All Quotations</PageTitle>
              <PageSubtitle>Manage and generate customer quotation packages.</PageSubtitle>
            </div>
          </HeroText>
          <PermissionGate permission="quotation.create">
            <SubmitButton
              type="button"
              $ready
              onClick={() => navigate("/dashboard/quotation/singleQuotation")}
              style={{ minWidth: 180, height: 46 }}
            >
              <Plus size={16} /> Create Quotation
            </SubmitButton>
          </PermissionGate>
        </HeroContent>
      </Hero>

      <ScrollPanel>
        <Toolbar>
          <ToolbarLeft>
            <SearchControl>
              <Search size={16} />
              <input
                value={searchVal}
                onChange={(event) => {
                  setSearchVal(event.target.value);
                }}
                placeholder="Search quotations"
              />
              {searchVal && (
                <ClearButton
                  type="button"
                  onClick={() => {
                    setSearchVal("");
                    setSearchText("");
                    setCurrentPage(1);
                  }}
                >
                  <X size={14} />
                </ClearButton>
              )}
            </SearchControl>

            <EntriesControl>
              <span>Show</span>
              <select
                value={entries}
                onChange={(event) => {
                  setEntries(Number(event.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </EntriesControl>
          </ToolbarLeft>

          <ToolbarRight>
            <TermsManageButton
              type="button"
              onClick={() => setIsStudioTermsModalOpen(true)}
              $hasTerms={hasStudioTerms}
              title={hasStudioTerms ? "Edit Studio Terms & Conditions" : "Add Studio Terms & Conditions"}
            >
              {hasStudioTerms ? <Pencil size={13} /> : <Plus size={13} />}
              <span>{hasStudioTerms ? "Edit Terms & Condition" : "Add Terms & Condition"}</span>
            </TermsManageButton>
            <ExportDropdown
              onCopy={handleCopy}
              onCSV={handleExportCSV}
              onExcel={handleExportExcel}
              onPDF={handleExportPDF}
              onPrint={() => window.print()}
            />
          </ToolbarRight>
        </Toolbar>

        <ListHeader style={{ padding: "0 22px" }}>
          <ListTitle>Quotation records</ListTitle>
          {!loading && (
            <CountBadge>
              {totalFilteredCount} record{totalFilteredCount !== 1 ? "s" : ""}
            </CountBadge>
          )}
        </ListHeader>

        {loading ? (
          <MarketplaceLoadingSpinner padding="48px 22px 60px" />
        ) : visibleQuotations.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={hasActiveSearch ? <SearchX size={36} strokeWidth={1.5} /> : <FileStack size={36} strokeWidth={1.5} />}
            title={hasActiveSearch ? "No quotations found" : "No quotations yet"}
            description={
              hasActiveSearch
                ? "Nothing matched your search. Try a different quotation number, customer name, or phone."
                : "Create your first quotation to share pricing packages with customers."
            }
            actions={[
              ...(hasActiveSearch
                ? [{
                  label: "Clear search",
                  onClick: () => { setSearchVal(""); setSearchText(""); setCurrentPage(1); },
                  variant: "secondary" as const,
                }]
                : []),
              ...(hasPermission("quotation.create")
                ? [{
                  label: hasActiveSearch ? "Create Quotation" : "Create Your First Quotation",
                  onClick: () => navigate("/dashboard/quotation/singleQuotation"),
                  icon: <Plus size={15} />,
                }]
                : []),
            ]}
            hint={!hasActiveSearch ? {
              icon: <Sparkles size={13} />,
              text: "Convert quotes to orders & share on WhatsApp",
            } : undefined}
          />
        ) : (
          <ScrollTable ref={scrollRef}>
            <TableWrap>
              <PaymentsTable $minWidth={980}>
                <thead>
                  <tr>
                    <th><span>Quotation No</span></th>
                    <th><span>Customer Name</span></th>
                    <th><span>Branch</span></th>
                    <th><span>Mobile</span></th>
                    <th><span>Reference</span></th>
                    <th><span>Place</span></th>
                    <th><span>Created Date</span></th>
                    <th><span>Grand Total (₹)</span></th>
                    <th style={{ textAlign: "right" }}><span>Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQuotations.map((q) => (
                    <tr key={q.id}>
                      <td>{q.quotation_no}</td>
                      <td>{q.customer_name}</td>
                      <td>
                        {q.branch_name ? (
                          <span style={{ fontSize: 11, background: '#e0e7ff', color: '#4338ca', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                            {q.branch_name}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                      <td>{q.customer_phone}</td>
                      <td>{q.customer_reference || "-"}</td>
                      <td>{q.customer_place || "-"}</td>
                      <td>{q.quotation_date}</td>
                      <td>
                        {q.without_price ? "-" : (
                          <AmountCell>₹{formatINR(q.grand_total)}</AmountCell>
                        )}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {hasAnyQuotationAction && (
                          <MenuContainer>
                            <ThreeDotsButton
                              ref={(el) => { menuButtonRefs.current[q.id] = el; }}
                              type="button"
                              aria-expanded={activeMenuId === q.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleMenu(q.id);
                              }}
                            >
                              <Dot />
                              <Dot />
                              <Dot />
                            </ThreeDotsButton>
                          </MenuContainer>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </PaymentsTable>
            </TableWrap>
          </ScrollTable>
        )}

        {!loading && visibleQuotations.length > 0 && <PanelDivider />}

        <FooterRow>
          <FooterText>
            Showing {totalFilteredCount === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
            {Math.min(currentPage * entries, totalFilteredCount)} of {totalFilteredCount} entries
          </FooterText>
          <Pagination>
            <PageButton
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </PageButton>
            {Array.from({ length: totalPages }).map((_, idx) => (
              <PageButton
                key={idx}
                $active={currentPage === idx + 1}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </PageButton>
            ))}
            <PageButton
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </PageButton>
          </Pagination>
        </FooterRow>
      </ScrollPanel>

      {activeMenuQuotation && menuCoords &&
        createPortal(
          <>
            <Backdrop
              onClick={() => {
                setActiveMenuId(null);
                setMenuCoords(null);
              }}
            />
            <DropdownMenu
              $fixed
              style={{
                top: menuCoords.top,
                bottom: menuCoords.bottom,
                right: menuCoords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {canViewQuotation && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    setActiveMenuId(null);
                    setMenuCoords(null);
                    navigate(`/dashboard/quotation/view?id=${activeMenuQuotation.id}`);
                  }}
                >
                  <ItemIconContainer>
                    <Eye size={14} />
                  </ItemIconContainer>
                  <ItemLabel>View Quotation</ItemLabel>
                </MenuItem>
              )}
              {canEditQuotation && (
                <MenuItem
                  type="button"
                  onClick={() => {
                    setActiveMenuId(null);
                    setMenuCoords(null);
                    navigate(`/dashboard/quotation/singleQuotation?id=${activeMenuQuotation.id}`);
                  }}
                >
                  <ItemIconContainer>
                    <Pencil size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Edit Quotation</ItemLabel>
                </MenuItem>
              )}
              {canConvertQuotation && (() => {
                const isConverted =
                  (activeMenuQuotation.status === "converted" || activeMenuQuotation.status === "accepted") &&
                  !revertedQuotationIds.includes(activeMenuQuotation.id);
                return isConverted ? (
                  <MenuItem
                    type="button"
                    onClick={() => {
                      setActiveMenuId(null);
                      setMenuCoords(null);
                      handleViewConvertedOrder(activeMenuQuotation);
                    }}
                  >
                    <ItemIconContainer>
                      <ArrowRightLeft size={14} style={{ color: "#31a66a" }} />
                    </ItemIconContainer>
                    <ItemLabel style={{ color: "#31a66a" }}>Converted to Order</ItemLabel>
                  </MenuItem>
                ) : (
                  <MenuItem
                    type="button"
                    onClick={(e) => {
                      setActiveMenuId(null);
                      setMenuCoords(null);
                      handleConvertToOrder(activeMenuQuotation.id, e);
                    }}
                  >
                    <ItemIconContainer>
                      <ArrowRightLeft size={14} />
                    </ItemIconContainer>
                    <ItemLabel>Convert to Order</ItemLabel>
                  </MenuItem>
                );
              })()}
              {canShareQuotation && (
                <MenuItem
                  type="button"
                  disabled={sharingQuotationId !== null}
                  onClick={() => {
                    setActiveMenuId(null);
                    setMenuCoords(null);
                    handleShareQuotation(activeMenuQuotation.id);
                  }}
                >
                  <ItemIconContainer>
                    {sharingQuotationId === activeMenuQuotation.id ? (
                      <Loader size={14} className="spin" />
                    ) : (
                      <MessageCircle size={14} />
                    )}
                  </ItemIconContainer>
                  <ItemLabel>
                    {sharingQuotationId === activeMenuQuotation.id ? "Sharing..." : "Share Quotation"}
                  </ItemLabel>
                </MenuItem>
              )}
              {canDeleteQuotation && (
                <MenuItem
                  type="button"
                  onClick={(e) => {
                    setActiveMenuId(null);
                    setMenuCoords(null);
                    handleDelete(activeMenuQuotation.id, e);
                  }}
                >
                  <ItemIconContainer>
                    <Trash2 size={14} />
                  </ItemIconContainer>
                  <ItemLabel>Delete Quotation</ItemLabel>
                </MenuItem>
              )}
            </DropdownMenu>
          </>,
          document.body
        )}

      {modalShareQuotation && (
        <QuotationShareModal
          quotation={modalShareQuotation}
          onClose={() => setModalShareQuotation(null)}
          onShareOfficial={handleOfficialShare}
          loading={sharingQuotationId === modalShareQuotation.id}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId !== null && (
        <DeletePopup
          text="Are you sure you want to delete this quotation?"
          buttonText="Delete"
          cancel={() => setDeleteConfirmId(null)}
          Delete={handleConfirmDelete}
        />
      )}

      {/* Convert confirmation modal */}
      {convertConfirmId !== null &&
        createPortal(
          <ModalOverlay onClick={() => !converting && setConvertConfirmId(null)}>
            <ModalShell style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
              <ModalHero>
                <HeroRow>
                  <ModalHeroText>
                    <ModalHeroBadge>
                      <ArrowRightLeft size={11} />
                      Convert to order
                    </ModalHeroBadge>
                    <ConfirmModalTitle>Convert Quotation?</ConfirmModalTitle>
                    <ModalSubtitle>
                      This will create an active order from the selected quotation.
                    </ModalSubtitle>
                  </ModalHeroText>
                  <CloseBtn
                    type="button"
                    onClick={() => !converting && setConvertConfirmId(null)}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </CloseBtn>
                </HeroRow>
              </ModalHero>
              <ModalBody>
                <ConvertBody>
                  Are you sure you want to convert this quotation into an active order?
                </ConvertBody>
              </ModalBody>
              <ConvertBtnRow>
                <ConvertCancelBtn
                  type="button"
                  onClick={() => setConvertConfirmId(null)}
                  disabled={converting}
                >
                  Cancel
                </ConvertCancelBtn>
                <PrimaryBtn type="button" onClick={handleConfirmConvert} disabled={converting}>
                  {converting ? "Converting..." : "Convert"}
                </PrimaryBtn>
              </ConvertBtnRow>
            </ModalShell>
          </ModalOverlay>,
          document.body
        )}

      {activeQuotationForShare && (
        <HiddenPrintContainer>
          <DocumentPrintTemplate
            type="quotation"
            activeDoc={activeQuotationForShare}
            user={user}
            studioLogo={studio_logo || business?.studio_logo}
            studioName={business?.studio_name}
            studioAddress={business?.address}
            printRef={sharePrintRef}
          />
        </HiddenPrintContainer>
      )}

      <ListReportPrintTemplate
        printRef={reportPrintRef}
        title="Quotations Report"
        subtitle="Customer quotation packages overview"
        user={user ? { name: user.name, phone_number: user.phone_number, email: user.email } : undefined}
        columns={[
          { key: 'quotation_no', label: 'Quotation No', width: '13%' },
          { key: 'quotation_date', label: 'Date', width: '12%' },
          { key: 'customer_name', label: 'Customer', width: '18%' },
          { key: 'customer_phone', label: 'Mobile', width: '13%' },
          { key: 'customer_reference', label: 'Reference', width: '12%' },
          { key: 'customer_place', label: 'Place', width: '14%' },
          {
            key: 'grand_total',
            label: 'Grand Total',
            align: 'right',
            width: '14%',
            render: (value) => (
              <CellBold>{String(value)}</CellBold>
            ),
          },
        ]}
        rows={quotationReportRows}
        summaryItems={quotationReportSummary}
      />

      <StudioTermsManageModal
        isOpen={isStudioTermsModalOpen}
        onClose={() => setIsStudioTermsModalOpen(false)}
      />
    </ScrollPage>
  );
};

export default AllQuotationPage;

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  box-sizing: border-box;
  overflow: hidden;

  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
`;

const ScrollPage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const ScrollPanel = styled(Panel)`
  flex: none;
  min-height: unset;
  overflow: visible;
`;

const ScrollTable = styled(PanelTableScroll)`
  flex: none;
  overflow-y: visible;
  min-height: unset;
`;

const PanelDivider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.06);
  margin: 0 22px;
  flex-shrink: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 12px;
  flex-shrink: 0;

  @media ${device.tablet} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EntriesControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.62);

  select {
    height: 38px;
    border: 1px solid rgba(23, 23, 23, 0.1);
    border-radius: 10px;
    padding: 0 12px;
    background: #fafafa;
    color: #171717;
    font-family: Urbanist, sans-serif;
    font-size: 13px;
    font-weight: 600;
    outline: none;
    cursor: pointer;
    &:focus { border-color: rgba(201, 77, 170, 0.35); }
  }
`;

const SearchControl = styled.label`
  width: min(100%, 320px);
  height: 46px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  color: rgba(23, 23, 23, 0.42);
  background: #fafafa;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  &:focus-within {
    background: #fff;
    border-color: rgba(201, 77, 170, 0.35);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    font-family: Urbanist, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #171717;
    &::placeholder {
      color: rgba(23, 23, 23, 0.35);
      font-weight: 500;
    }
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 22px 22px;
  flex-shrink: 0;

  @media ${device.tablet} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FooterText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.1)")};
  border-radius: 10px;
  padding: 0 12px;
  background: ${(p) => (p.$active ? gradient : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23,23,23,0.65)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 4px 12px rgba(201, 77, 170, 0.22)" : "none")};
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: ${(p) => (p.$active ? "#fff" : "#c94daa")};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const ConvertBody = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.68);
  line-height: 1.65;
`;

const ConvertBtnRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 24px 24px;
`;

const ConvertCancelBtn = styled.button`
  min-width: 110px;
  height: 46px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 12px;
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const ClearButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.42);
  padding: 0;
  &:hover { color: #171717; }
`;

const HiddenPrintContainer = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  width: 800px;
  height: auto;
  overflow: hidden;
  background: white;
  pointer-events: none;
`;

const TermsManageButton = styled.button<{ $hasTerms?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.3)" : "rgba(23, 23, 23, 0.12)")};
  background: ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.05)" : "#ffffff")};
  color: ${({ $hasTerms }) => ($hasTerms ? "#c94daa" : "#171717")};
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $hasTerms }) => ($hasTerms ? "rgba(201, 77, 170, 0.1)" : "#f8f9fa")};
    border-color: ${({ $hasTerms }) => ($hasTerms ? "#c94daa" : "rgba(23, 23, 23, 0.2)")};
    transform: translateY(-1px);
  }
`;